import type { NpcEncounter, NpcUnit } from "./types";
import {
  DAMAGE_VARIANCE_MAX,
  DAMAGE_VARIANCE_MIN,
  HIT_CHANCE,
  type HitChanceCurve,
  WAVE_GAP_SECONDS
} from "./combatConstants";
import { DEFAULT_SHIP_VS_DRONE_SHIP_WEIGHT, DRONE_HEAL_SECONDS } from "./droneSystem";
import { parseCompositionUnits, parseStageSegments } from "./encounterComposition";
import type { PlayerCombatStats } from "./missionDifficulty";

export type Rng = () => number; // uniform [0, 1)

export interface CombatPlayerStats extends PlayerCombatStats {
  // Combined shield-regen + hull-repair rate, applied only during inter-wave
  // recovery gaps (16.30) -- not modeled during active combat, since no
  // ship in the roster repairs faster than incoming DPS in a real fight.
  regenPerS: number;
}

export interface PlayerShipState {
  hp: number;
  maxHp: number;
  dps: number;
  regenPerS: number;
}

export interface EnemyUnitState {
  id: string;
  name: string;
  hp: number;
  maxHp: number;
  dps: number;
  repairPerS: number; // 0 if the unit has no self/remote repair (16.13)
}

export interface DroneInput {
  maxHp: number;
  dps: number;
}

export interface DroneState {
  id: string;
  hp: number;
  maxHp: number;
  dps: number;
  // false = recalled/docked: not fighting, not targetable, healing at
  // maxHp/45s during recovery gaps only (16.23/16.30).
  deployed: boolean;
}

export type CombatOutcome = "ongoing" | "won" | "lost" | "fled";

export interface CombatState {
  tick: number;
  player: PlayerShipState;
  waves: EnemyUnitState[][];
  currentWaveIndex: number;
  activeUnits: EnemyUnitState[];
  gapTicksRemaining: number;
  targetLockId: string | null;
  drones: DroneState[];
  droneTargetLockId: string | null;
  // Enemy AI's ship-vs-drone weighting, 0-1 (16.23): probability an alive
  // enemy targets the player ship rather than a randomly-chosen deployed
  // drone. A v1 starting default, tunable by feel (CLAUDE.md).
  shipVsDroneShipWeight: number;
  outcome: CombatOutcome;
  log: string[];
}

function buildWave(segment: string, encounter: NpcEncounter, units: NpcUnit[]): EnemyUnitState[] {
  const counts = parseCompositionUnits(segment, encounter.faction, encounter.hullTier, units);
  const instances: EnemyUnitState[] = [];
  for (const { unit, count } of counts) {
    for (let i = 0; i < count; i++) {
      const maxHp = unit.hullHp + unit.shieldHp;
      instances.push({
        id: `${unit.name}#${instances.length}`,
        name: unit.name,
        hp: maxHp,
        maxHp,
        dps: unit.dps,
        repairPerS: unit.selfRemoteRepairHpPerS ?? 0
      });
    }
  }
  return instances;
}

export interface InitCombatOptions {
  drones?: DroneInput[]; // deployed at full HP; build via droneSystem.ts's DRONE_STATS
  shipVsDroneShipWeight?: number;
}

// Initializes a fresh combat encounter against wave 1 -- 16.30's tick loop
// operates on the state this returns, one tick at a time (see tick() below).
// Fleet is out of scope (non-operational in v1, 16.19) -- every enemy's
// damage this tick either targets the player ship or a deployed drone.
export function initCombat(
  player: CombatPlayerStats,
  encounter: NpcEncounter,
  units: NpcUnit[],
  options: InitCombatOptions = {}
): CombatState {
  const segments = parseStageSegments(encounter.composition);
  const waves = segments.map((segment) => buildWave(segment, encounter, units));
  const drones: DroneState[] = (options.drones ?? []).map((d, i) => ({
    id: `drone#${i}`,
    hp: d.maxHp,
    maxHp: d.maxHp,
    dps: d.dps,
    deployed: true
  }));

  return {
    tick: 0,
    player: { hp: player.ehp, maxHp: player.ehp, dps: player.dps, regenPerS: player.regenPerS },
    waves,
    currentWaveIndex: 0,
    activeUnits: waves[0],
    gapTicksRemaining: 0,
    targetLockId: null,
    drones,
    droneTargetLockId: null,
    shipVsDroneShipWeight: options.shipVsDroneShipWeight ?? DEFAULT_SHIP_VS_DRONE_SHIP_WEIGHT,
    outcome: "ongoing",
    log: [`Tick 0: ${encounter.id} begins -- wave 1/${waves.length} (${waves[0].length} units)`]
  };
}

// One attack roll (16.14), given a specific hit chance: on a hit, a uniform
// 0.50-1.49x damage multiplier; a miss deals exactly zero damage.
export function rollDamageWithHitChance(baseDps: number, hitChance: number, rng: Rng): number {
  if (rng() >= hitChance) {
    return 0;
  }
  const multiplier = DAMAGE_VARIANCE_MIN + rng() * (DAMAGE_VARIANCE_MAX - DAMAGE_VARIANCE_MIN);
  return baseDps * multiplier;
}

// Flat 90% hit chance (16.14) -- used wherever no real distance/range pair
// is available (combatResolutionLoop's own abstracted tick() below has no
// positions). The live Expedition View instead calls rollDamageWithHitChance
// directly with a real rangeHitChance() result (16.66).
export function rollDamage(baseDps: number, rng: Rng): number {
  return rollDamageWithHitChance(baseDps, HIT_CHANCE, rng);
}

// Range-based hit chance (16.66, extends 16.14): linear falloff from a
// curve's ceil at distance 0 to its floor at distance === rangeM. Distance
// beyond rangeM clamps to the floor rather than continuing to fall --
// callers only invoke this once a target is already confirmed in range.
export function rangeHitChance(distanceM: number, rangeM: number, curve: HitChanceCurve): number {
  const t = Math.min(1, Math.max(0, rangeM > 0 ? distanceM / rangeM : 0));
  return curve.ceil - (curve.ceil - curve.floor) * t;
}

// Offense/Defense targeting priority (16.30, reusing 16.23's drone-defense
// logic as the template for both): (1) continue the current lock if still
// alive, (2) otherwise the highest-healing/self-repairing enemy present,
// (3) otherwise the highest-DPS enemy present. A manual override (if
// provided and still alive) bypasses all three tiers, mirroring the
// player-weapon/drone auto/manual toggle. Drones share one target lock and
// use this same priority (16.30: "the same underlying principle... now
// governs every targeting context").
export function pickOffenseTarget(
  activeUnits: EnemyUnitState[],
  currentLockId: string | null,
  manualTargetId?: string | null
): string | null {
  const alive = activeUnits.filter((u) => u.hp > 0);
  if (alive.length === 0) {
    return null;
  }

  if (manualTargetId) {
    const manual = alive.find((u) => u.id === manualTargetId);
    if (manual) {
      return manual.id;
    }
  }

  if (currentLockId) {
    const current = alive.find((u) => u.id === currentLockId);
    if (current) {
      return current.id;
    }
  }

  const healers = alive.filter((u) => u.repairPerS > 0);
  if (healers.length > 0) {
    return healers.reduce((best, u) => (u.repairPerS > best.repairPerS ? u : best)).id;
  }

  return alive.reduce((best, u) => (u.dps > best.dps ? u : best)).id;
}

export interface TickOptions {
  rng?: Rng;
  manualTargetId?: string | null;
  droneManualTargetId?: string | null;
}

function deployedAlive(drones: DroneState[]): DroneState[] {
  return drones.filter((d) => d.deployed && d.hp > 0);
}

// Advances combat by exactly one second (16.30). No-op once the encounter
// has already resolved (won/lost/fled).
export function tick(state: CombatState, options: TickOptions = {}): CombatState {
  const rng = options.rng ?? Math.random;

  if (state.outcome !== "ongoing") {
    return state;
  }

  const tickNumber = state.tick + 1;

  // Inter-wave recovery gap (16.12/16.30): zero incoming damage, player
  // regenerates at its own rate (capped at max); recalled/docked drones
  // heal at maxHp/45s, proportional to the gap length (16.23/16.30). The
  // gap timer counts down.
  if (state.gapTicksRemaining > 0) {
    const player = {
      ...state.player,
      hp: Math.min(state.player.maxHp, state.player.hp + state.player.regenPerS)
    };
    const drones = state.drones.map((d) =>
      d.deployed || d.hp >= d.maxHp
        ? d
        : { ...d, hp: Math.min(d.maxHp, d.hp + d.maxHp / DRONE_HEAL_SECONDS) }
    );
    const gapTicksRemaining = state.gapTicksRemaining - 1;

    if (gapTicksRemaining === 0) {
      const nextWaveIndex = state.currentWaveIndex + 1;
      return {
        ...state,
        tick: tickNumber,
        player,
        drones,
        gapTicksRemaining: 0,
        currentWaveIndex: nextWaveIndex,
        activeUnits: state.waves[nextWaveIndex],
        targetLockId: null,
        droneTargetLockId: null,
        log: [
          ...state.log,
          `Tick ${tickNumber}: recovery gap ends -- wave ${nextWaveIndex + 1}/${state.waves.length} arrives`
        ]
      };
    }

    return {
      ...state,
      tick: tickNumber,
      player,
      drones,
      gapTicksRemaining,
      log: [...state.log, `Tick ${tickNumber}: recovery gap (${gapTicksRemaining}s remaining)`]
    };
  }

  // Active combat: the player's weapon fires once at its target; every
  // deployed, alive drone independently fires once at the drones' shared
  // target; every alive enemy unit independently fires once, at either the
  // player ship or a randomly-chosen deployed drone (16.30's ship-vs-drone
  // weighting, 16.23).
  const targetId = pickOffenseTarget(state.activeUnits, state.targetLockId, options.manualTargetId);
  let activeUnits = state.activeUnits;
  if (targetId) {
    const damage = rollDamage(state.player.dps, rng);
    activeUnits = activeUnits.map((u) =>
      u.id === targetId ? { ...u, hp: Math.max(0, u.hp - damage) } : u
    );
  }

  const droneTargetId = deployedAlive(state.drones).length
    ? pickOffenseTarget(activeUnits, state.droneTargetLockId, options.droneManualTargetId)
    : null;
  if (droneTargetId) {
    for (const drone of deployedAlive(state.drones)) {
      const damage = rollDamage(drone.dps, rng);
      activeUnits = activeUnits.map((u) =>
        u.id === droneTargetId ? { ...u, hp: Math.max(0, u.hp - damage) } : u
      );
    }
  }

  let playerHp = state.player.hp;
  let drones = state.drones;
  for (const enemy of activeUnits) {
    if (enemy.hp <= 0) {
      continue;
    }
    const candidates = deployedAlive(drones);
    const targetsDrone = candidates.length > 0 && rng() >= state.shipVsDroneShipWeight;
    const damage = rollDamage(enemy.dps, rng);
    if (targetsDrone) {
      const chosen = candidates[Math.min(Math.floor(rng() * candidates.length), candidates.length - 1)];
      drones = drones.map((d) => (d.id === chosen.id ? { ...d, hp: Math.max(0, d.hp - damage) } : d));
    } else {
      playerHp -= damage;
    }
  }
  playerHp = Math.max(0, playerHp);
  drones = drones.filter((d) => d.hp > 0); // destroyed drones are a permanent loss (16.23)
  const player = { ...state.player, hp: playerHp };

  if (playerHp <= 0) {
    return {
      ...state,
      tick: tickNumber,
      player,
      activeUnits,
      drones,
      targetLockId: targetId,
      droneTargetLockId: droneTargetId,
      outcome: "lost",
      log: [...state.log, `Tick ${tickNumber}: player ship destroyed`]
    };
  }

  const waveCleared = activeUnits.every((u) => u.hp <= 0);
  if (waveCleared) {
    const isLastWave = state.currentWaveIndex === state.waves.length - 1;
    if (isLastWave) {
      return {
        ...state,
        tick: tickNumber,
        player,
        activeUnits,
        drones,
        targetLockId: null,
        droneTargetLockId: null,
        outcome: "won",
        log: [...state.log, `Tick ${tickNumber}: final wave cleared -- victory`]
      };
    }
    return {
      ...state,
      tick: tickNumber,
      player,
      activeUnits,
      drones,
      gapTicksRemaining: WAVE_GAP_SECONDS,
      targetLockId: null,
      droneTargetLockId: null,
      log: [
        ...state.log,
        `Tick ${tickNumber}: wave ${state.currentWaveIndex + 1}/${state.waves.length} cleared -- recovery gap begins`
      ]
    };
  }

  return {
    ...state,
    tick: tickNumber,
    player,
    activeUnits,
    drones,
    targetLockId: targetId,
    droneTargetLockId: droneTargetId,
    log: [...state.log, `Tick ${tickNumber}: combat continues`]
  };
}

// Recalling a drone pulls it out of the fight (not targetable, not
// attacking) so it can heal during the next recovery gap (16.23).
export function recallDrone(state: CombatState, droneId: string): CombatState {
  return {
    ...state,
    drones: state.drones.map((d) => (d.id === droneId ? { ...d, deployed: false } : d))
  };
}

export function redeployDrone(state: CombatState, droneId: string): CombatState {
  return {
    ...state,
    drones: state.drones.map((d) => (d.id === droneId ? { ...d, deployed: true } : d))
  };
}

export interface FleeAttemptResult {
  success: boolean;
  successChancePercent: number;
  state: CombatState;
}

// SUPERSEDED by Section 2a: fleeing is now Fast Travel (turn -> accelerate
// -> exit, always succeeds barring destruction mid-sequence), not a
// probability roll -- this function and its speed-based % chance are no
// longer the locked model. Left in place (still tested) since nothing
// currently depends on removing it, but new flee/disengage UI (Expedition
// View's Fast Travel) must NOT call this.
//
// Flee/disengage (16.30, tied to Ship Speed 16.28): attemptable on any tick.
// Success chance = 50% + (speed multiplier - 1.0) x 100%, clamped [10%, 95%].
// A failed attempt costs that tick -- the enemy gets a free hit (only
// during active combat; there's nothing to hit during a recovery gap).
export function attemptFlee(
  state: CombatState,
  shipSpeedMultiplier: number,
  rng: Rng = Math.random
): FleeAttemptResult {
  if (state.outcome !== "ongoing") {
    return { success: false, successChancePercent: 0, state };
  }

  const raw = 50 + (shipSpeedMultiplier - 1.0) * 100;
  const successChancePercent = Math.min(95, Math.max(10, raw));
  const success = rng() * 100 < successChancePercent;
  const tickNumber = state.tick + 1;

  if (success) {
    return {
      success: true,
      successChancePercent,
      state: {
        ...state,
        tick: tickNumber,
        outcome: "fled",
        log: [...state.log, `Tick ${tickNumber}: flee succeeded (${successChancePercent}% chance)`]
      }
    };
  }

  let playerHp = state.player.hp;
  if (state.gapTicksRemaining === 0) {
    for (const unit of state.activeUnits) {
      if (unit.hp > 0) {
        playerHp -= rollDamage(unit.dps, rng);
      }
    }
    playerHp = Math.max(0, playerHp);
  }

  const outcome: CombatOutcome = playerHp <= 0 ? "lost" : "ongoing";

  return {
    success: false,
    successChancePercent,
    state: {
      ...state,
      tick: tickNumber,
      player: { ...state.player, hp: playerHp },
      outcome,
      log: [
        ...state.log,
        `Tick ${tickNumber}: flee failed (${successChancePercent}% chance) -- enemy got a free hit`
      ]
    }
  };
}
